// ===========================================
// BUSTED! BLACKJACK - CHECKOUT SUCCESS PAGE
// ===========================================

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Lottie from 'lottie-react';
import { api } from '../api/client';
import { useAuthStore } from '../stores';
import { formatBalance } from '../utils/format';
import spinningCoinAnimation from '../assets/spinning-coin.json';

type PurchaseType = 'chips' | 'buddy';

interface ChipsPurchase {
  type: 'chips';
  chips: number;
  newBalance: number;
}

interface BuddyPurchase {
  type: 'buddy';
  expiresAt: string;
}

type PurchaseDetails = ChipsPurchase | BuddyPurchase;

export function CheckoutSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchUser } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [purchaseDetails, setPurchaseDetails] = useState<PurchaseDetails | null>(null);

  useEffect(() => {
    const verifyPayment = async () => {
      const sessionId = searchParams.get('session_id');
      // Detect buddy purchase from URL or try both
      const purchaseType = searchParams.get('type') as PurchaseType | null;

      if (!sessionId) {
        setError('No session ID found');
        setIsLoading(false);
        return;
      }

      try {
        // Try buddy verification first if indicated, otherwise try chips
        const result = await api.payments.verifyPayment(sessionId, purchaseType || undefined);

        if (result.success && result.data) {
          if (result.data.type === 'buddy') {
            setPurchaseDetails({
              type: 'buddy',
              expiresAt: result.data.expiresAt || '',
            });
          } else {
            setPurchaseDetails({
              type: 'chips',
              chips: result.data.chips || 0,
              newBalance: result.data.newBalance || 0,
            });
          }
          // Refresh user data to update balance/buddy status in the store
          fetchUser();
        } else {
          setError(result.error?.message || 'Failed to verify payment');
        }
      } catch (err) {
        setError('Failed to verify payment');
      } finally {
        setIsLoading(false);
      }
    };

    verifyPayment();
  }, [searchParams, fetchUser]);

  // Auto-redirect after 5 seconds
  useEffect(() => {
    if (purchaseDetails) {
      const timer = setTimeout(() => {
        navigate('/');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [purchaseDetails, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-casino-gold-500 mx-auto mb-4"></div>
          <p className="text-white text-xl">Verifying payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 max-w-md mx-4 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 border-4 border-red-500 flex items-center justify-center">
            <span className="text-3xl font-bold text-red-500">!</span>
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Payment Issue</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <p className="text-gray-500 text-sm mb-6">
            If you were charged, your B-Bucks will be credited within a few minutes.
            Please contact support if the issue persists.
          </p>
          <button
            onClick={() => navigate('/')}
            className="w-full py-3 bg-casino-gold-500 hover:bg-casino-gold-400 text-gray-900 font-bold rounded-xl transition-colors"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  // Format buddy expiration
  const formatExpiresAt = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Chips purchase success
  if (purchaseDetails?.type === 'chips') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 max-w-md mx-4 text-center">
          {/* Success Animation */}
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-500/20 border-4 border-green-500 flex items-center justify-center">
            <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h2 className="text-2xl font-bold text-white mb-2">Purchase Complete!</h2>
          <p className="text-gray-400 mb-6">Your B-Bucks have been credited</p>

          {/* Chips received */}
          <div className="bg-casino-gold-500/10 border border-casino-gold-500/30 rounded-lg p-6 mb-6">
            <p className="text-casino-gold-400 text-sm mb-1">You received</p>
            <div className="flex items-center justify-center gap-2">
              <p className="text-4xl font-bold text-casino-gold-400">
                {purchaseDetails.chips.toLocaleString('en-US')}
              </p>
              <Lottie
                animationData={spinningCoinAnimation}
                loop={true}
                className="w-12 h-12"
              />
            </div>
            <p className="text-casino-gold-400 text-sm mt-1">B-Bucks!</p>
          </div>

          {/* New balance */}
          <div className="bg-gray-700/50 rounded-lg p-4 mb-6">
            <p className="text-gray-400 text-sm">New Balance</p>
            <p className="text-2xl font-bold text-white">
              {formatBalance(purchaseDetails.newBalance)}
            </p>
          </div>

          {/* Actions */}
          <button
            onClick={() => navigate('/')}
            className="w-full py-3 bg-casino-gold-500 hover:bg-casino-gold-400 text-gray-900 font-bold rounded-xl transition-colors"
          >
            Start Playing!
          </button>

          <p className="text-gray-500 text-xs mt-4">
            Redirecting in 5 seconds...
          </p>

          <p className="text-gray-600 text-[10px] mt-3 leading-snug">
            B-Bucks are virtual play chips for entertainment only. They have no cash value and cannot be redeemed, withdrawn, or exchanged for money.
          </p>
        </div>
      </div>
    );
  }

  // Buddy subscription success
  if (purchaseDetails?.type === 'buddy') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 max-w-md mx-4 text-center">
          {/* Success Animation */}
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-500/20 border-4 border-green-500 flex items-center justify-center">
            <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h2 className="text-2xl font-bold text-white mb-2">Buddy Activated!</h2>
          <p className="text-gray-400 mb-6">Your BUSTED! Buddy is now ready</p>

          {/* Buddy active */}
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-6 mb-6">
            <div className="text-4xl mb-2">🎰</div>
            <p className="text-green-400 font-bold text-lg">BUSTED! Buddy</p>
            <p className="text-green-400/70 text-sm mt-1">Active</p>
          </div>

          {/* Expiration */}
          <div className="bg-gray-700/50 rounded-lg p-4 mb-6">
            <p className="text-gray-400 text-sm">Active Until</p>
            <p className="text-xl font-bold text-white">
              {formatExpiresAt(purchaseDetails.expiresAt)}
            </p>
          </div>

          {/* Actions */}
          <button
            onClick={() => navigate('/')}
            className="w-full py-3 bg-green-600 hover:bg-green-500 text-white font-bold rounded-xl transition-colors"
          >
            Start Playing with Buddy!
          </button>

          <p className="text-gray-500 text-xs mt-4">
            Redirecting in 5 seconds...
          </p>
        </div>
      </div>
    );
  }

  // Fallback (shouldn't happen)
  return null;
}

export default CheckoutSuccessPage;
